import React, { useEffect, useState } from "react";
import { Copy, Download, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SCard, SRow } from "./SettingsUI";
import api from "@/lib/api";
import { toast } from "sonner";
import { translateFr as tfr } from "@/i18n";

const copyText = async (text, okMsg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMsg);
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(okMsg);
    } catch {
      toast.error(tfr("Copie impossible — sélectionnez le texte manuellement"));
    }
  }
};

const CodesDisplay = ({ codes, onDone }) => {
  const copy = () => copyText(codes.join("\n"), tfr("Codes copiés"));
  const download = () => {
    const url = URL.createObjectURL(new Blob([`${tfr("Codes de récupération Athlynx")}\n\n${codes.join("\n")}\n`], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url; a.download = "athlynx_codes_recuperation.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="recovery-codes-display">
      <p className="text-sm text-muted-foreground">{tfr("Conservez ces codes dans un endroit sûr. Ils permettent d'accéder à votre compte si vous perdez votre application d'authentification.")} <strong>{tfr("Ils ne seront plus affichés.")}</strong></p>
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-white/[0.08] bg-black/20 p-4 font-mono text-sm text-center">
        {codes.map((c) => <span key={c} data-testid="recovery-code">{c}</span>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={copy} data-testid="codes-copy-btn"><Copy className="h-3.5 w-3.5 mr-1.5" />{tfr("Copier")}</Button>
        <Button variant="outline" size="sm" onClick={download} data-testid="codes-download-btn"><Download className="h-3.5 w-3.5 mr-1.5" />{tfr("Télécharger")}</Button>
      </div>
      <Button className="w-full" onClick={onDone} data-testid="codes-saved-btn">{tfr("J'ai enregistré mes codes de récupération")}</Button>
    </div>
  );
};

const EnrollDialog = ({ open, onOpenChange, hasPassword, onEnabled }) => {
  const [step, setStep] = useState(0); // 0 reauth, 1 qr, 2 codes
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setStep(0); setPassword(""); setSetup(null); setCode(""); setCodes(null); setShowKey(false); } }, [open]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/security/2fa/totp/start", { password: password || null });
      setSetup(r.data); setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/security/2fa/totp/verify", { code });
      setCodes(r.data.recovery_codes); setStep(2);
      toast.success(tfr("La double authentification est maintenant activée."));
    } catch (e) { toast.error(e?.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && step === 2) onEnabled?.(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[460px]" data-testid="totp-enroll-dialog">
        <DialogHeader>
          <DialogTitle>{step === 2 ? tfr("Codes de récupération") : tfr("Configurer une application d'authentification")}</DialogTitle>
          <DialogDescription>
            {step === 0 && tfr("Confirmez votre identité pour commencer.")}
            {step === 1 && tfr("1. Ouvrez votre application (Google Authenticator, Authy, 1Password…) · 2. Scannez le QR code · 3. Saisissez le code à 6 chiffres.")}
            {step === 2 && tfr("Dernière étape : enregistrez vos codes de récupération.")}
          </DialogDescription>
        </DialogHeader>
        {step === 0 && (
          <div className="space-y-4">
            {hasPassword ? (
              <div className="grid gap-2">
                <Label>{tfr("Mot de passe actuel")}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="enroll-password-input" autoComplete="current-password" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{tfr("Votre compte utilise la connexion Google : cliquez sur Continuer pour démarrer la configuration.")}</p>
            )}
            <Button className="w-full" onClick={start} disabled={busy || (hasPassword && !password)} data-testid="enroll-start-btn">
              {busy ? tfr("Vérification…") : tfr("Continuer")}
            </Button>
          </div>
        )}
        {step === 1 && setup && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <img src={setup.qr} alt="QR code d'authentification" className="h-44 w-44 rounded-xl border border-white/[0.1] bg-white p-1.5" data-testid="totp-qr" />
            </div>
            <button type="button" onClick={() => setShowKey(!showKey)} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mx-auto block" data-testid="show-setup-key-btn">
              Impossible de scanner le QR code ?
            </button>
            {showKey && (
              <div className="rounded-xl border border-white/[0.08] bg-black/20 p-3 text-center space-y-1" data-testid="manual-setup-key">
                <p className="text-[11px] text-muted-foreground">Compte : {setup.account} · Émetteur : Athlynx</p>
                <p className="font-mono text-sm tracking-wider">{setup.manual_key}</p>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => copyText(setup.secret, tfr("Clé copiée"))} data-testid="copy-setup-key-btn">
                  <Copy className="h-3 w-3 mr-1" />Copier la clé
                </Button>
              </div>
            )}
            <div className="grid gap-2">
              <Label>{tfr("Code de vérification")}</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" maxLength={6}
                className="text-center text-lg tracking-[0.4em]" data-testid="enroll-code-input" />
            </div>
            <Button className="w-full" onClick={verify} disabled={busy || code.length !== 6} data-testid="enroll-verify-btn">
              {busy ? tfr("Vérification…") : tfr("Vérifier et activer")}
            </Button>
          </div>
        )}
        {step === 2 && codes && <CodesDisplay codes={codes} onDone={() => { onOpenChange(false); onEnabled?.(); }} />}
      </DialogContent>
    </Dialog>
  );
};

const ManageDialog = ({ open, onOpenChange, hasPassword, onChanged }) => {
  const [mode, setMode] = useState(null); // regen | disable
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMode(null); setPassword(""); setCode(""); setCodes(null); } }, [open]);

  const regen = async () => {
    setBusy(true);
    try {
      const r = await api.post("/security/2fa/recovery-codes/regenerate", { password: password || null });
      setCodes(r.data.recovery_codes);
      toast.success(tfr("Nouveaux codes générés — les anciens sont invalidés"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/security/2fa/disable", { password: password || null, code });
      toast.success(tfr("Double authentification désactivée"));
      onOpenChange(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && codes) onChanged?.(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="totp-manage-dialog">
        <DialogHeader>
          <DialogTitle>{tfr("Gérer la double authentification")}</DialogTitle>
          <DialogDescription>{tfr("Régénérez vos codes de récupération ou désactivez la double authentification.")}</DialogDescription>
        </DialogHeader>
        {codes ? (
          <CodesDisplay codes={codes} onDone={() => { onOpenChange(false); onChanged?.(); }} />
        ) : !mode ? (
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => setMode("regen")} data-testid="manage-regen-btn">
              <KeyRound className="h-4 w-4 mr-2" /> Générer de nouveaux codes de récupération
            </Button>
            <Button variant="outline" className="w-full justify-start border-red-500/30 text-red-400 hover:bg-red-500/[0.08] hover:text-red-400" onClick={() => setMode("disable")} data-testid="manage-disable-btn">
              {tfr("Désactiver la double authentification")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {mode === "regen" && <p className="text-sm text-muted-foreground">{tfr("Attention : vos codes de récupération actuels seront tous invalidés.")}</p>}
            {mode === "disable" && <p className="text-sm text-muted-foreground">{tfr("Votre compte n'exigera plus de code d'authentification à la connexion.")}</p>}
            {hasPassword && (
              <div className="grid gap-2">
                <Label>{tfr("Mot de passe")}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="manage-password-input" autoComplete="current-password" />
              </div>
            )}
            {mode === "disable" && (
              <div className="grid gap-2">
                <Label>{tfr("Code d'authentification ou code de récupération")}</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={tfr("000000 ou XXXX-XXXX")} data-testid="manage-code-input" />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMode(null)}>{tfr("Retour")}</Button>
              {mode === "regen" ? (
                <Button onClick={regen} disabled={busy || (hasPassword && !password)} data-testid="manage-regen-confirm">{busy ? tfr("Génération…") : tfr("Générer")}</Button>
              ) : (
                <Button variant="destructive" onClick={disable} disabled={busy || !code || (hasPassword && !password)} data-testid="manage-disable-confirm">{busy ? tfr("Désactivation…") : tfr("Désactiver")}</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const TwoFactorManager = ({ overview, reload }) => {
  const s = overview?.security || {};
  const hasPassword = Boolean(s.has_password);
  const [enroll, setEnroll] = useState(false);
  const [manage, setManage] = useState(false);

  return (
    <SCard title={tfr("Authentification à deux facteurs")} desc={tfr("Ajoutez une couche de sécurité supplémentaire à votre compte Athlynx. Cette protection est optionnelle et reste sous votre contrôle.")}
      testid="twofa-card" right={s.two_factor_enabled ? <ShieldCheck className="h-4 w-4 text-green-500" /> : null}>
      {s.two_factor_enabled ? (
        <>
          <SRow label="Application d'authentification" value={s.two_factor_methods?.includes("totp") ? tfr("Activée") : tfr("Non configurée")} testid="twofa-totp-row" />
          {s.two_factor_methods?.includes("email") && <SRow label={tfr("Vérification par email")} value={tfr("Activée")} testid="twofa-email-row" />}
          <SRow label={tfr("Codes de récupération")} value={`${s.recovery_codes_remaining ?? 0} ${(s.recovery_codes_remaining ?? 0) > 1 ? tfr("restants") : tfr("restant")}`} testid="twofa-codes-row" />
          <div className="flex justify-end mt-4">
            <Button variant="outline" size="sm" onClick={() => setManage(true)} data-testid="twofa-manage-btn">{tfr("Gérer")}</Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{tfr("Protégez votre compte avec une application d'authentification (Google Authenticator, Authy, 1Password…).")}</p>
          <Button size="sm" onClick={() => setEnroll(true)} data-testid="twofa-enable-btn">Activer la double authentification</Button>
        </div>
      )}
      <EnrollDialog open={enroll} onOpenChange={setEnroll} hasPassword={hasPassword} onEnabled={reload} />
      <ManageDialog open={manage} onOpenChange={setManage} hasPassword={hasPassword} onChanged={reload} />
    </SCard>
  );
};

export const SecurityAlertsCard = () => {
  const [alerts, setAlerts] = useState(null);
  useEffect(() => { api.get("/security/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  if (!alerts) return null;

  const toggle = async (key, enabled) => {
    setAlerts((a) => ({ ...a, optional: a.optional.map((o) => o.key === key ? { ...o, enabled } : o) }));
    try { await api.patch("/security/alerts", { [key]: enabled }); }
    catch { toast.error(tfr("Erreur")); }
  };

  return (
    <SCard title={tfr("Alertes de sécurité")} desc={alerts.email_configured
      ? tfr("Soyez notifié par email lors d'actions importantes sur votre compte.")
      : tfr("Les alertes email seront envoyées dès que l'envoi d'emails sera configuré (clé Resend). Vos préférences sont enregistrées dès maintenant.")}
      testid="security-alerts-card">
      <div className="space-y-1">
        {alerts.optional.map((o) => (
          <div key={o.key} className="flex items-center justify-between py-2 border-b border-white/[0.05]">
            <span className="text-sm">{o.label}</span>
            <Switch checked={o.enabled} onCheckedChange={(v) => toggle(o.key, v)} data-testid={`alert-toggle-${o.key}`} />
          </div>
        ))}
        {alerts.required.map((label) => (
          <div key={label} className="flex items-center justify-between py-2 border-b border-white/[0.05] last:border-0">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="text-[11px] text-muted-foreground rounded-full border border-white/[0.1] px-2 py-0.5" data-testid="alert-required-badge">{tfr("Toujours activée")}</span>
          </div>
        ))}
      </div>
    </SCard>
  );
};
